import axios from "axios";

// ── Lazy store accessor ──────────────────────────────────────────────────────
// We do NOT import `store` at the top level because axiosInstance is imported
// by slices, which are imported by the store's root reducer — a circular chain
// that causes "Cannot access X before initialization" errors.
//
// Instead we use a setter that the store calls once it is fully created, so
// by the time any interceptor fires the reference is always available.
let _store = null;
export const injectStore = (store) => { _store = store; };

// ── Lazy UIAnalyticsSlice accessor (same reason — avoids circular import) ───
let _uiAnalyticsEvents = null;
export const injectUiAnalyticsEvents = (fn) => { _uiAnalyticsEvents = fn; };

const baseUrl = process.env.REACT_APP_BASE_URL;

// ---------------------------------------------------------------------------
// Helper — classify the error into a category and a specific type
// ---------------------------------------------------------------------------
const classifyError = (error) => {
  // ── Category 1: No response at all (network / browser level) ──────────────
  if (!error.response) {
    // Request was made but no response received
    if (error.request) {
      if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
        return { errorCategory: "NETWORK", errorType: "TIMEOUT" };
      }
      if (error.message?.toLowerCase().includes("network error")) {
        // Could be CORS, no-internet, DNS — browser gives the same generic message.
        // We use a best-guess heuristic:
        //   navigator.onLine = false → no internet
        //   otherwise              → likely CORS / SSL (browser hides the detail)
        if (!navigator.onLine) {
          return { errorCategory: "NETWORK", errorType: "NO_INTERNET" };
        }
        return { errorCategory: "NETWORK", errorType: "CORS_OR_SSL" };
      }
      if (error.code === "ERR_CANCELED" || error.message?.toLowerCase().includes("cancel")) {
        return { errorCategory: "NETWORK", errorType: "REQUEST_ABORTED" };
      }
      return { errorCategory: "NETWORK", errorType: "UNKNOWN_NETWORK" };
    }

    // Request was never sent (JS setup error — bad URL, circular payload etc.)
    return { errorCategory: "CLIENT_CODE", errorType: "REQUEST_SETUP_FAILED" };
  }

  // ── Category 2: Server returned an HTTP error status ──────────────────────
  const status = error.response.status;

  const SERVER_STATUS_MAP = {
    400: "BAD_REQUEST",
    401: "UNAUTHORIZED",
    403: "FORBIDDEN",
    404: "NOT_FOUND",
    409: "CONFLICT",
    422: "UNPROCESSABLE_ENTITY",
    429: "RATE_LIMITED",
    500: "INTERNAL_SERVER_ERROR",
    502: "BAD_GATEWAY",
    503: "SERVICE_UNAVAILABLE",
    504: "GATEWAY_TIMEOUT",
  };

  return {
    errorCategory: "SERVER_ERROR",
    errorType: SERVER_STATUS_MAP[status] || `HTTP_${status}`,
    httpStatus: status,
  };
};

// ---------------------------------------------------------------------------
// Helper — extract the token from a path-based "checkout-link" URL
// (e.g. https://pl.boxpay.in/<token>, no ?token= query string). Mirrors the
// extraction in BoxPayCheckout.js so error reports from this flow still
// carry a callerToken instead of resolving to null.
//
// TEMP-DISABLED — holding off while we ship a prod issue; re-enable (and
// wire back into buildApiErrorPayload's callerToken fallback below) once
// that's out.
// ---------------------------------------------------------------------------
// const NON_TOKEN_PATHS = [
//   "/",
//   "/finalStatus",
//   "/payment-completion-handler",
//   "/payment-notify",
//   "/airwallex/3ds",
//   "/fpp/3ds",
//   "/psp-checkout-handler",
// ];

// const getPathBasedToken = () => {
//   const pathname = window.location.pathname;
//   if (NON_TOKEN_PATHS.includes(pathname)) return null;

//   const lastSlashIndex = pathname.lastIndexOf("/");
//   if (lastSlashIndex === -1) return null;

//   return pathname.substring(lastSlashIndex + 1) || null;
// };

// ---------------------------------------------------------------------------
// Helper — build the analytics payload
// ---------------------------------------------------------------------------
const buildApiErrorPayload = (error) => {
  const { errorCategory, errorType, httpStatus } = classifyError(error);

  const state = _store?.getState();
  const callerToken =
    state?.paymentSessionDetails?.sessionDetails?.token ||
    new URLSearchParams(window.location.search).get("token") ||
    // getPathBasedToken() ||
    null;

  // Pull the clearest available error message
  const errorMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.statusText ||
    error?.message ||
    "Unknown error";

  // Server-side field validation errors (e.g. from your getErrorMessage helper)
  const fieldErrors =
    error?.response?.data?.fieldErrorItems?.map((item) => item.message) || [];

  return {
    callerToken,
    uiEvent: "WEB_CHECKOUT_CRASH",
    eventAttrs: {
      // ── Error classification ──────────────────────────────────────────────
      type: errorCategory,   // "NETWORK" | "SERVER_ERROR" | "CLIENT_CODE"
      message: `${errorType}${httpStatus ? ` (${httpStatus})` : ""} — ${errorMessage}`,

      // ── Request info ──────────────────────────────────────────────────────
      url: error?.config?.url || null,
      method: error?.config?.method?.toUpperCase() || null,

      // ── Error detail ──────────────────────────────────────────────────────
      httpStatus: httpStatus || null,
      errorType,
      fieldErrors: fieldErrors.length ? fieldErrors : null,

      // ── Context ───────────────────────────────────────────────────────────
      timestamp: new Date().toISOString(),
      pageUrl: window.location.href,
    },
  };
};

// ---------------------------------------------------------------------------
// Errors to ignore — (endpoint, status codes) pairs that should NOT fire
// WEB_CHECKOUT_CRASH. Add an entry per endpoint you want to silence.
// `urlIncludes` is matched against error.config.url via String.includes().
// ---------------------------------------------------------------------------
const IGNORED_API_ERRORS = [
  { urlIncludes: "/postal-codes", statusCodes: [404] },
];

const isIgnoredApiError = (error) => {
  const url = error?.config?.url || "";
  const status = error?.response?.status;
  return IGNORED_API_ERRORS.some(
    ({ urlIncludes, statusCodes }) =>
      url.includes(urlIncludes) && statusCodes.includes(status)
  );
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const axiosInstance = axios.create({
  baseURL: baseUrl,
});

// ── Response interceptor ────────────────────────────────────────────────────
axiosInstance.interceptors.response.use(
  // Success — pass through untouched
  (response) => response,

  // Error — classify, build payload, fire analytics, then re-throw
  (error) => {
    try {
      // Skip sending analytics for the analytics call itself to avoid infinite loop
      const isAnalyticsCall = error?.config?.url?.includes("/ui-analytics");
      if (!isAnalyticsCall && !isIgnoredApiError(error) && _store && _uiAnalyticsEvents) {
        const payload = buildApiErrorPayload(error);
        _store.dispatch(_uiAnalyticsEvents(payload));
      }
    } catch (analyticsError) {
      // Never let the analytics logic break the actual app flow
      console.error("Failed to send API error analytics:", analyticsError);
    }

    // Always re-throw so individual slices still handle errors as before
    return Promise.reject(error);
  }
);

export default axiosInstance;
